'use client'

import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import axios from 'axios'
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { useRouter } from 'next/navigation'
import React, { useState, ReactNode } from 'react'

import { SURVEY_DETAIL_QUERY_KEY } from '../apis/queries/use-get-detail-survey-query'
import { useAppSelector } from '../hooks/use-app-selector'
import useToast from '../hooks/use-toast'

type TReactQuery = {
  readonly children: ReactNode
}

// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function makeQueryClient(toast: any, router: AppRouterInstance, partnerId?: string) {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry(failureCount, error) {
          // prevent duplicate refetch with refresh token
          if (axios.isAxiosError(error) && error?.response?.status === 401) {
            return false
          }

          return failureCount < 2
        },
        refetchOnMount: true,
        staleTime: 0,
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError(error, query) {
        if (toast?.show && axios.isAxiosError(error)) {
          if (query?.queryKey[0] === SURVEY_DETAIL_QUERY_KEY && error?.response?.status === 404) {
            router.replace('/survey/finish/result/survey-not-found')

            return
          }

          toast?.show({
            title: error?.response?.data?.error?.code || 'Error Occured',
            description:
              error?.response?.data?.error?.message ||
              'Sorry, we are currently experiencing an issue.',
            traceId: error?.response?.data?.traceId || '',
            type: 'error',
          })
        }
      },
    }),
  })
}

let browserQueryClient: QueryClient | undefined = undefined

function getQueryClient(toast: any, router: AppRouterInstance, partnerId?: string) {
  if (typeof window === 'undefined') {
    return makeQueryClient(toast, router, partnerId)
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient(toast, router, partnerId)
    return browserQueryClient
  }
}

export default function ReactQuery({ children }: TReactQuery) {
  const toast = useToast()
  const router = useRouter()
  const { partnerId } = useAppSelector((state) => state.user)
  const [queryClient] = useState(getQueryClient(toast, router, partnerId))

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NEXT_PUBLIC_ENABLE_REACT_QUERY_DEV_TOOLS && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}
